import { ReactNode, useCallback, useEffect } from 'react';
import {
  createFileRoute,
  Navigate,
  redirect,
  useRouter,
} from '@tanstack/react-router';
import { Heading, Text } from 'react-aria-components';
import { z } from 'zod';
import { ApolloError, useMutation } from '@apollo/client';
import PageLoader from 'src/components/ui/PageLoader';
import RootLayout from 'src/components/layout/RootLayout';
import Button from 'src/components/ui/Button';
import { useAuth } from 'src/utils/auth';
import VERIFY_EMAIL_MUTATION from 'src/graphql/mutation/verify-email.graphql';
import Logo from 'src/components/ui/Logo';
import useUser from 'src/hooks/useUser';
import LinkButton from 'src/components/ui/LinkButton';
import useSkeletonCleanup from 'src/init/useSkeletonCleanup';

import type { ClientToken } from 'src/types/client';

const VerifyEmailPage = () => {
  const { email, code } = Route.useSearch();
  const router = useRouter();
  const auth = useAuth();

  const [verifyEmail, { data, loading, error }] = useMutation(
    VERIFY_EMAIL_MUTATION,
    {
      variables: { input: { email, code } },
    }
  );
  useSkeletonCleanup();

  const onSignIn = useCallback(async () => {
    auth.onLogin(data?.verifyEmail as ClientToken);
    await router.invalidate();
  }, [auth, data?.verifyEmail, router]);

  useEffect(() => {
    const abortController = new AbortController();

    const callback = async () =>
      verifyEmail({
        context: {
          fetchOptions: { signal: abortController.signal },
        },
      });

    // Apollo error link handles errors; suppress rejection to avoid Sentry duplicate via onunhandledrejection
    callback().catch(() => {});

    return () => abortController.abort();
  }, [verifyEmail]);

  if (loading) return <PageLoader />;
  if (error) return <ErrorContent />;

  return <SuccessContent onSignIn={onSignIn} />;
};

const Container = ({ children }: { children: ReactNode }) => (
  <RootLayout>
    <div className="w-full max-w-[26.75rem] mx-auto bg-surface-alt rounded-3xl shadow-3 text-center p-11">
      <Logo className="mb-11" />
      {children}
    </div>
  </RootLayout>
);

const ErrorContent = ({ error }: { error?: ApolloError }) => {
  const router = useRouter();
  const { user, loading } = useUser();
  const errors = error?.graphQLErrors[0]?.extensions?.fields ?? {};
  const errorText = Object.values(errors).join(', ');

  if (loading) return <PageLoader />;

  return (
    <Container>
      <Heading level={2} className="text-surface-alt-90 text-2xl font-bold">
        {user?.isEmailVerified
          ? 'Email already verified!'
          : 'Email not verified!'}
      </Heading>
      <Text
        elementType="p"
        className="text-surface-alt-75 font-medium mt-4 mb-8"
      >
        {errorText}
      </Text>
      <LinkButton
        reversed
        onPress={() =>
          router.navigate({
            to: user?.clientId ? '/profile' : '/login',
            replace: true,
          })
        }
      >
        Back to {user?.clientId ? 'Profile' : 'Sign In'}
      </LinkButton>
    </Container>
  );
};

const SuccessContent = ({ onSignIn }: { onSignIn: () => void }) => {
  const { user, loading } = useUser();

  if (loading) return <PageLoader />;

  if (user?.clientId) return <Navigate to="/" />;

  return (
    <Container>
      <Heading level={2} className="text-surface-alt-90 text-2xl font-bold">
        Email Verified!
      </Heading>
      <Text
        elementType="p"
        className="text-surface-alt-75 font-medium mt-4 mb-8"
      >
        Sign In to get the process rolling!
      </Text>

      <Button onPress={onSignIn}>Sign In</Button>
    </Container>
  );
};

const VerifySchema = z.object({
  email: z.string().email(),
  code: z.string().length(64),
});

export const Route = createFileRoute('/verify-email')({
  validateSearch: (search) => VerifySchema.parse(search),
  beforeLoad: async ({ search }) => {
    const { success } = VerifySchema.safeParse(search);

    if (!success) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({
        to: '/login',
        replace: true,
      });
    }
  },
  component: VerifyEmailPage,
});
